import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { gql } from '@apollo/client';
import TableWindow from '../../components/Table/TableWindow';
import useTablePages from '../../hooks/useTablePages';
import availableColumns from './Users.columns';
import { Flex } from 'rebass';
import { useOrderBy } from '../../hooks/useOrderBy';
import PageBar from '../../components/PageBar/PageBar';
import { RouteComponentProps, withRouter } from 'react-router';
import { PortalWithState } from 'react-portal';
import useMode from '../../hooks/useMode';
import UserEditModal from '../../components/User/UserEditModal';
import FilterBarUser from './FilterBarUser';
import Button from '../../components/Button/Button';
import Layout from '../../components/Layout/Layout';
import useColumnConfiguration from '../../hooks/useColumnConfiguration';
import useSessionInfo from '../../hooks/useSessionInfo';
import { ContactProfileInput } from '../../components/Profile/types';
import AdminUserUpdateModal from '../../components/User/AdminUserUpdateModal';
import { UserStatus } from 'components/User/types';

const GET_USER_IDS = gql`
  query GetUserIds(
    $where: JSON
    $orderBy: [ColumnOrder]
    $limit: Int
    $offset: Int
  ) {
    users(
      where: $where
      orderBy: $orderBy
      limit: $limit
      offset: $offset
      groupBy: ["profileId"]
    ) {
      profileId
      profile(required: true) {
        id
      }
      status
    }
  }
`;

const GET_USERS = gql`
  query GetUsers(
    $where: JSON
    $orderBy: [ColumnOrder]
    $limit: Int
    $offset: Int
  ) {
    users(where: $where, orderBy: $orderBy, limit: $limit, offset: $offset) {
      profileId
      profile(required: true) {
        id
        type
        name
        firstName
        lastName
      }
      roles
      email
      status
      archived
    }
  }
`;

const GET_USERS_ADMIN = gql`
  query GetUsersAdmin(
    $where: JSON
    $orderBy: [ColumnOrder]
    $limit: Int
    $offset: Int
  ) {
    users(where: $where, orderBy: $orderBy, limit: $limit, offset: $offset) {
      profileId
      profile(required: true) {
        id
        type
        name
        firstName
        lastName
      }
      roles {
        companyProfileId
        role
      }
      email
      status
    }
  }
`;

export type UserItem = {
  profileId: string;
  profile: ContactProfileInput;
  roles: string[];
  email: string;
  status: UserStatus;
};

export type UserItemAdmin = {
  profileId: string;
  profile: ContactProfileInput;
  roles: { companyProfileId: string; role: string }[];
  email: string;
  status: UserStatus;
};

type UserDetailParams = {
  id?: string;
};

type UserDetailProps = RouteComponentProps<UserDetailParams>;

/**
 * @component for testers page
 */
function Users({ match }: UserDetailProps): JSX.Element {
  const [filters, setFilters] = useState<Record<string, unknown> | null>({});
  const { session } = useSessionInfo();

  const mode = useMode();

  const { orderBy, onHeaderClick } = useOrderBy(
    'users',
    availableColumns,
    'profile.name'
  );

  const { loading, error, itemIds, selectedIds, setSelectedIds, itemLoader } =
    useTablePages<UserItem, 'profileId'>(
      'users',
      GET_USER_IDS,
      {
        where: {
          ...filters,
          profileId: session?.companyProfile
            ? {
                ne: session?.companyProfile?.id,
              }
            : undefined,
        },
        limit: null,
        orderBy,
      },
      mode === 'admin' ? GET_USERS_ADMIN : GET_USERS,
      (ids) => ({
        where: {
          profileId: ids,
        },
        limit: null,
        orderBy,
      }),
      'profileId'
    );

  const [user, setUser] = useState();

  const { visibleColumns, columnConfiguration, setColumnConfiguration } =
    useColumnConfiguration(availableColumns, 'users');

  return (
    <Layout>
      <Flex
        flexGrow={1}
        flexDirection="column"
        px="mainContent"
        pb="mainContent"
      >
        <PageBar title="Users">
          {mode !== 'admin' && (
            <PortalWithState closeOnEsc>
              {({ openPortal, closePortal, portal }) => (
                <>
                  <Button
                    ml={3}
                    variant="primary"
                    icon="plus"
                    onClick={openPortal}
                  >
                    Add User
                  </Button>
                  {portal(
                    <UserEditModal
                      action="add"
                      onClose={closePortal}
                      refetchQueries={[
                        GET_USER_IDS,
                        GET_USERS_ADMIN,
                        GET_USERS,
                      ]}
                    />
                  )}
                </>
              )}
            </PortalWithState>
          )}
        </PageBar>
        <FilterBarUser filters={filters ?? {}} setFilters={setFilters} />
        {loading && itemIds.length === 0 ? (
          'Loading...'
        ) : (
          <>
            <PortalWithState closeOnEsc>
              {({ openPortal, closePortal, portal }) => {
                const handleClick = (user: any) => {
                  setUser(user);
                  openPortal();
                };
                return (
                  <>
                    <TableWindow
                      columns={visibleColumns}
                      itemIds={itemIds}
                      itemLoader={itemLoader}
                      onSelectionChange={setSelectedIds}
                      selectedIds={selectedIds}
                      onHeaderClick={onHeaderClick}
                      orderBy={orderBy}
                      onRowClick={handleClick}
                      columnConfiguration={columnConfiguration}
                      setColumnConfiguration={setColumnConfiguration}
                    />
                    {portal(
                      mode === 'admin' ? (
                        <AdminUserUpdateModal
                          onClose={closePortal}
                          user={user}
                          refetchQueries={[
                            GET_USER_IDS,
                            GET_USERS_ADMIN,
                            GET_USERS,
                          ]}
                        />
                      ) : (
                        <UserEditModal
                          action="edit"
                          onClose={closePortal}
                          user={user}
                          refetchQueries={[
                            GET_USER_IDS,
                            GET_USERS_ADMIN,
                            GET_USERS,
                          ]}
                        />
                      )
                    )}
                  </>
                );
              }}
            </PortalWithState>
          </>
        )}
      </Flex>
    </Layout>
  );
}

Users.propTypes = {
  match: PropTypes.any,
};

export default withRouter(Users);
